import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Camera, Upload, ScanLine, ArrowLeft, RefreshCw, 
  Sparkles, Maximize2, AlertTriangle, Briefcase, Building2,
  Mail, Phone, MapPin, Globe, Copy, Check, Edit2, Trash2, X, Plus, Calendar
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import TagPicker from "../components/TagPicker";
import { Camera as CapCamera } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { scheduleLeadReminder } from "../services/notificationService";
import { saveLocalLead } from "../lib/leadCache";

// Synchronized with backend MAX_IMAGE_WIDTH/HEIGHT (1200px)
const MAX_CARD_DIMENSION = 1200;

// Standard ISO/IEC 7810 ID-1 card ratio (3.370 in × 2.125 in = 1.586)
const ASPECT_RATIOS = {
  horizontal: 1.586,
  square: 1.0,
  vertical: 0.63,
};

export default function Capture() {
  const navigate = useNavigate();

  // Refs
  const fileRef = useRef(null);
  const backFileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const imgRef = useRef(null);
  const backImgRef = useRef(null);
  const isMountedRef = useRef(true);
  const copyTimeoutRef = useRef(null);

  // Flow State
  const [stage, setStage] = useState("idle"); // "idle" | "streaming" | "crop" | "scanning" | "review"
  const [cardOrientation, setCardOrientation] = useState("horizontal");
  const [isScanning, setIsScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Front Card State
  const [imageData, setImageData] = useState(null);
  const [finalImage, setFinalImage] = useState(null);
  const [crop, setCrop] = useState({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
  const [completedCrop, setCompletedCrop] = useState(null);

  // Back Card State
  const [backImageData, setBackImageData] = useState(null);
  const [backFinalImage, setBackFinalImage] = useState(null);
  const [backCrop, setBackCrop] = useState({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
  const [completedBackCrop, setCompletedBackCrop] = useState(null);
  const [isCapturingBack, setIsCapturingBack] = useState(false);

  // Extracted Lead Form Data
  const [form, setForm] = useState({
    name: "",
    company: "",
    job_title: "",
    email: "",
    email2: "",
    phone: "",
    phone2: "",
    address: "",
    website: "",
  });
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState("");
  const [reminder, setReminder] = useState("");

  // Clean Camera Stream Unmount
  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      });
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopCameraStream();
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, [stopCameraStream]);

  // Video Stream Attacher
  useEffect(() => {
    if (stage === "streaming" && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [stage]);

  // Clipboard Copy
  const copyToClipboard = async (text, keyName) => {
    if (!text) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setCopiedKey(keyName);
      toast.success(`Copied ${keyName}`);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) setCopiedKey(null);
      }, 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const getErrorMessage = (err) => {
    if (!err) return "An unexpected error occurred.";
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ");
    if (err.message) return err.message;
    return "Failed to process card";
  };

  // Preset Ratio Calculations
  const calculateAutoCardCrop = (width, height, orientation = cardOrientation) => {
    const targetRatio = ASPECT_RATIOS[orientation] || 1.586;
    let cropWidth, cropHeight;

    if (width / height > targetRatio) {
      cropHeight = height * 0.86;
      cropWidth = cropHeight * targetRatio;
    } else {
      cropWidth = width * 0.90;
      cropHeight = cropWidth / targetRatio;
    }

    cropWidth = Math.min(width, Math.max(50, cropWidth));
    cropHeight = Math.min(height, Math.max(50, cropHeight));

    const x = Math.max(0, (width - cropWidth) / 2);
    const y = Math.max(0, (height - cropHeight) / 2);

    return {
      pixel: { x, y, width: cropWidth, height: cropHeight, unit: "px" },
      percent: {
        x: (x / width) * 100,
        y: (y / height) * 100,
        width: (cropWidth / width) * 100,
        height: (cropHeight / height) * 100,
        unit: "%",
      },
    };
  };

  const handleApplyPresetCrop = (orientation, isBack = false) => {
    setCardOrientation(orientation);
    const targetRef = isBack ? backImgRef : imgRef;
    if (!targetRef.current) return;

    const { naturalWidth, naturalHeight } = targetRef.current;
    const autoBox = calculateAutoCardCrop(naturalWidth, naturalHeight, orientation);

    if (isBack) {
      setBackCrop(autoBox.percent);
      setCompletedBackCrop(autoBox.pixel);
    } else {
      setCrop(autoBox.percent);
      setCompletedCrop(autoBox.pixel);
    }
    toast.success(`${orientation.toUpperCase()} frame applied`);
  };

  const handleResetFullImageCrop = (isBack = false) => {
    const fullPercent = { unit: "%", x: 0, y: 0, width: 100, height: 100 };
    if (isBack) {
      setBackCrop(fullPercent);
      setCompletedBackCrop(null);
    } else {
      setCrop(fullPercent);
      setCompletedCrop(null);
    }
    toast.info("Using full image");
  };

  const handleFile = (file, isBack = false) => {
    if (!file) return;
    if (file.type && !/(jpeg|jpg|png|webp|heic|heif|image)/i.test(file.type)) {
      toast.error("Please choose a valid JPEG, PNG, or WEBP photo");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      if (isBack) {
        setBackImageData(dataUrl);
        setBackCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
      } else {
        setImageData(dataUrl);
        setCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
        setStage("crop");
      }
    };
    reader.onerror = () => toast.error("Could not read photo");
    reader.readAsDataURL(file);
  };

  const handleOpenCamera = async (e, capturingBack = false) => {
    if (e) e.preventDefault();
    setIsCapturingBack(capturingBack);

    if (Capacitor.isNativePlatform()) {
      try {
        const permissions = await CapCamera.checkPermissions();
        if (permissions.camera !== "granted") {
          const request = await CapCamera.requestPermissions();
          if (request.camera !== "granted") {
            toast.error("Camera permission is required to capture cards.");
            return;
          }
        }
      } catch (err) {
        console.warn("Capacitor camera check skipped:", err);
      }
    }

    try {
      stopCameraStream();
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = mediaStream;
      setStage("streaming");
    } catch {
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        streamRef.current = fallbackStream;
        setStage("streaming");
      } catch {
        toast.error("Unable to access camera. Please allow permissions or upload an image.");
      }
    }
  };

  const captureFrame = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Camera is initializing, please try in a moment.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    stopCameraStream();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.90);

    if (isCapturingBack) {
      setBackImageData(dataUrl);
      setBackCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
      setIsCapturingBack(false);
      setStage("crop");
    } else {
      setImageData(dataUrl);
      setCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
      setStage("crop");
    }
  };

  // Boundary-Safe, Memory-Efficient Downsampling Canvas (1200px max)
  const getCroppedImg = (sourceImageData, targetImgRef, activeCrop) => {
    if (!targetImgRef || !targetImgRef.current) return sourceImageData;

    const image = targetImgRef.current;
    if (!image.naturalWidth || !image.naturalHeight) {
      return sourceImageData;
    }

    let sx = 0;
    let sy = 0;
    let sWidth = image.naturalWidth;
    let sHeight = image.naturalHeight;

    if (activeCrop && activeCrop.unit === "%") {
      sx = (activeCrop.x / 100) * image.naturalWidth;
      sy = (activeCrop.y / 100) * image.naturalHeight;
      sWidth = (activeCrop.width / 100) * image.naturalWidth;
      sHeight = (activeCrop.height / 100) * image.naturalHeight;
    } else if (activeCrop && activeCrop.width > 0 && activeCrop.height > 0) {
      const renderedWidth = image.width || image.naturalWidth;
      const renderedHeight = image.height || image.naturalHeight;
      const scaleX = image.naturalWidth / renderedWidth;
      const scaleY = image.naturalHeight / renderedHeight;

      if (activeCrop.width > renderedWidth) {
        sx = activeCrop.x;
        sy = activeCrop.y;
        sWidth = activeCrop.width;
        sHeight = activeCrop.height;
      } else {
        sx = activeCrop.x * scaleX;
        sy = activeCrop.y * scaleY;
        sWidth = activeCrop.width * scaleX;
        sHeight = activeCrop.height * scaleY;
      }
    }

    // Protect bounds against canvas DOM exception
    sx = Math.max(0, Math.min(sx, image.naturalWidth - 10));
    sy = Math.max(0, Math.min(sy, image.naturalHeight - 10));
    sWidth = Math.max(10, Math.min(sWidth, image.naturalWidth - sx));
    sHeight = Math.max(10, Math.min(sHeight, image.naturalHeight - sy));

    let destWidth = Math.floor(sWidth);
    let destHeight = Math.floor(sHeight);

    // Limit maximum dimension to 1200px before uploading
    if (Math.max(destWidth, destHeight) > MAX_CARD_DIMENSION) {
      const scale = MAX_CARD_DIMENSION / Math.max(destWidth, destHeight);
      destWidth = Math.max(1, Math.floor(destWidth * scale));
      destHeight = Math.max(1, Math.floor(destHeight * scale));
    }

    try {
      const canvas = document.createElement("canvas");
      canvas.width = destWidth;
      canvas.height = destHeight;
      const ctx = canvas.getContext("2d");

      // Fill white background to prevent dark transparency defects
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, destWidth, destHeight);

      ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, destWidth, destHeight);

      const croppedBase64 = canvas.toDataURL("image/jpeg", 0.85);
      return croppedBase64 && croppedBase64.length > 500 ? croppedBase64 : sourceImageData;
    } catch (err) {
      console.warn("Crop canvas drawing error:", err);
      return sourceImageData;
    }
  };

  // OCR Scan with 45s Timeout for Full Cascade Completion
  const handleScanImage = async (e) => {
    if (e) e.preventDefault();
    if (isScanning) return;
    if (!imageData) {
      toast.error("Please capture or upload a card image first.");
      return;
    }

    setDuplicateWarning(null);
    setIsScanning(true);

    const activeCropToUse = completedCrop || crop;
    let base64ToScanFront = getCroppedImg(imageData, imgRef, activeCropToUse);
    setFinalImage(base64ToScanFront);

    let base64ToScanBack = null;
    if (backImageData) {
      const activeBackCropToUse = completedBackCrop || backCrop;
      base64ToScanBack = getCroppedImg(backImageData, backImgRef, activeBackCropToUse);
      setBackFinalImage(base64ToScanBack);
    }

    setStage("scanning");

    try {
      const payload = { image_base64: base64ToScanFront };
      if (base64ToScanBack) {
        payload.back_image_base64 = base64ToScanBack;
      }

      const { data } = await api.post("/ocr/scan", payload, { timeout: 45000 });

      if (isMountedRef.current) {
        setForm({
          name: data.name || "",
          company: data.company || "",
          job_title: data.job_title || "",
          email: data.email || "",
          email2: data.email2 || "",
          phone: data.phone || "",
          phone2: data.phone2 || "",
          address: data.address || "",
          website: data.website || "",
        });
        setStage("review");

        const filled = ["name", "company", "job_title", "email", "phone", "address", "website"].filter(
          (k) => data[k]
        ).length;
        if (filled === 0) {
          toast.warning("Could not clearly read text — please review manually.");
        } else {
          toast.success(`Extracted ${filled} detail field${filled === 1 ? "" : "s"}!`);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 503 || status === 504) {
        toast.error(detail || "AI scanning pipeline is busy. Please retry in a few moments.");
      } else if (detail === "SCAN_LIMIT_REACHED" || (typeof detail === "string" && detail.includes("EVENT_PASS_LIMIT_REACHED"))) {
        toast.error("Event pass scan limit reached! Please upgrade your plan.");
        navigate("/payment");
      } else {
        toast.error(getErrorMessage(err));
      }
      if (isMountedRef.current) setStage("review");
    } finally {
      if (isMountedRef.current) setIsScanning(false);
    }
  };

  // Lead Save Pipeline
  const save = async (e) => {
    if (e) e.preventDefault();
    if (saving) return;

    setDuplicateWarning(null);

    if (!form.name && !form.email && !form.phone) {
      toast.error("Please provide at least a contact name, email, or phone number.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        tags,
        notes,
        image_base64: finalImage || imageData,
      };

      if (backFinalImage || backImageData) {
        payload.back_image_base64 = backFinalImage || backImageData;
      }

      if (reminder) {
        const remDate = new Date(reminder);
        if (!isNaN(remDate.getTime())) {
          payload.reminder_at = remDate.toISOString();
        }
      }

      const { data } = await api.post("/leads", payload);

      if (data?.id) {
        saveLocalLead(data).catch(() => {});
      }

      if (reminder && data?.id) {
        try {
          await scheduleLeadReminder({
            leadId: data.id,
            leadName: form.name,
            company: form.company,
            reminderDate: new Date(reminder),
            notes: notes || undefined,
          });
        } catch (notifErr) {
          console.warn("Could not schedule device reminder:", notifErr);
        }
      }

      if (data?.whatsapp_dispatched) {
        toast.success("Lead Saved & WhatsApp Brochure Dispatched! 📲", {
          description: data.whatsapp_message || `Delivered to ${form.phone}`,
        });
      } else if (form.phone) {
        toast.success("Lead saved to pipeline!", {
          description: "WhatsApp broadcast skipped (verify phone number or message quota).",
        });
      } else {
        toast.success("Lead successfully saved!");
      }

      navigate(`/leads/${data.id}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409 || (typeof detail === "string" && detail.startsWith("DUPLICATE_LEAD"))) {
        const cleanMsg =
          typeof detail === "string"
            ? detail.replace("DUPLICATE_LEAD:", "").trim()
            : "This lead already exists in your team's active pipeline.";
        setDuplicateWarning(cleanMsg);
        toast.error("Duplicate Lead Detected", { description: cleanMsg, duration: 6000 });
      } else if (detail === "SCAN_LIMIT_REACHED" || (typeof detail === "string" && detail.includes("EVENT_PASS_LIMIT_REACHED"))) {
        toast.error("Scan limit reached! Please upgrade your plan.");
        navigate("/payment");
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const reset = (e) => {
    if (e) e.preventDefault();
    stopCameraStream();
    setIsCapturingBack(false);

    setImageData(null);
    setFinalImage(null);
    setCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
    setCompletedCrop(null);

    setBackImageData(null);
    setBackFinalImage(null);
    setBackCrop({ unit: "%", x: 5, y: 15, width: 90, height: 57 });
    setCompletedBackCrop(null);

    setForm({
      name: "",
      company: "",
      job_title: "",
      email: "",
      email2: "",
      phone: "",
      phone2: "",
      address: "",
      website: "",
    });
    setTags([]);
    setNotes("");
    setReminder("");
    setDuplicateWarning(null);
    setStage("idle");
  };

  return (
    <div className="pb-36 px-4 pt-3 max-w-md mx-auto font-sans antialiased text-slate-900 relative min-h-screen">
      {/* Top Header */}
      <div className="flex items-center justify-between py-3 mb-3 sticky top-0 z-30 bg-slate-50/80 backdrop-blur-2xl border-b border-white/60">
        <button
          type="button"
          onClick={(e) => (stage === "idle" ? navigate(-1) : reset(e))}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/80 hover:bg-white text-xs font-bold text-slate-700 hover:text-blue-600 border border-slate-200/80 shadow-xs active:scale-95 transition-all cursor-pointer"
        >
          <ArrowLeft className="h-3.5 w-3.5 text-blue-600" /> {stage === "idle" ? "Back" : "Start over"}
        </button>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600 bg-blue-50/90 border border-blue-200/80 px-3 py-1 rounded-full shadow-2xs">
          {stage === "idle"
            ? "Scan Lead"
            : stage === "crop"
            ? "Crop & Adjust"
            : stage === "scanning"
            ? "Extracting…"
            : stage === "streaming"
            ? "Live Camera"
            : "Review Lead"}
        </span>
      </div>

      {/* Stage: Idle */}
      {stage === "idle" && (
        <div className="px-1 mt-4 animate-in fade-in duration-200">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-950 mb-2">
            Scan a <span className="text-blue-600">business card</span>.
          </h1>
          <p className="text-zinc-500 text-sm font-medium mb-6 leading-snug">
            Use your camera or upload a photo. We'll extract the details instantly.
          </p>

          <div className="relative w-full aspect-[1.6/1] bg-[#0c0c0e] rounded-[24px] p-5 flex flex-col items-center justify-center shadow-xl mb-6 overflow-hidden">
            <div className="absolute top-4 left-4 w-7 h-7 border-t-[3px] border-l-[3px] border-white rounded-tl-[4px]" />
            <div className="absolute top-4 right-4 w-7 h-7 border-t-[3px] border-r-[3px] border-white rounded-tr-[4px]" />
            <div className="absolute bottom-4 left-4 w-7 h-7 border-b-[3px] border-l-[3px] border-white rounded-bl-[4px]" />
            <div className="absolute bottom-4 right-4 w-7 h-7 border-b-[3px] border-r-[3px] border-white rounded-br-[4px]" />

            <div className="flex flex-col items-center gap-3.5 z-10">
              <div className="relative flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-blue-500 animate-pulse"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 3H5a2 2 0 0 0-2 2v2" />
                  <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                  <path d="M17 21h2a2 2 0 0 0 2-2v-2" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
              <p className="font-mono text-xs sm:text-[13px] font-medium text-zinc-400 tracking-tight text-center">
                Position front card inside the frame
              </p>
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onClick={(e) => {
              e.target.value = null;
            }}
            onChange={(e) => handleFile(e.target.files?.[0], false)}
          />

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={(e) => handleOpenCamera(e, false)}
              className="h-14 w-full bg-[#0c0c0e] hover:bg-zinc-900 active:scale-[0.98] text-white text-[15px] font-bold rounded-2xl flex items-center justify-center gap-2.5 shadow-lg shadow-zinc-950/15 transition-all cursor-pointer"
            >
              <Camera className="h-5 w-5" /> Open Live Camera
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                fileRef.current?.click();
              }}
              className="h-14 w-full bg-zinc-100 hover:bg-zinc-200 active:scale-[0.98] text-zinc-950 text-[15px] font-bold rounded-2xl flex items-center justify-center gap-2.5 transition-all cursor-pointer"
            >
              <Upload className="h-5 w-5 text-zinc-700" /> Upload Card
            </button>
          </div>
        </div>
      )}

      {/* Stage: Live Camera Stream */}
      {stage === "streaming" && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
              {isCapturingBack ? "Align Card Back" : "Align Card Front"}
            </span>

            <div className="flex bg-white/90 p-1 rounded-full border border-slate-200 shadow-2xs">
              <button
                type="button"
                onClick={() => setCardOrientation("horizontal")}
                className={`px-3 py-1 text-[11px] font-bold rounded-full transition-all cursor-pointer ${
                  cardOrientation === "horizontal"
                    ? "bg-blue-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                Horizontal (3.5×2)
              </button>
              <button
                type="button"
                onClick={() => setCardOrientation("square")}
                className={`px-3 py-1 text-[11px] font-bold rounded-full transition-all cursor-pointer ${
                  cardOrientation === "square"
                    ? "bg-blue-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                Square (1:1)
              </button>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden bg-black relative max-h-[62vh] border border-slate-800 shadow-2xl flex items-center justify-center">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full max-h-[62vh] object-cover" />

            <div
              className={`absolute transition-all duration-300 pointer-events-none ${
                cardOrientation === "horizontal"
                  ? "w-[92%] aspect-[1.586/1]"
                  : "w-[80%] aspect-square"
              } border-2 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]`}
            >
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-md" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-md" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-md" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-md" />
            </div>
          </div>

          <button
            type="button"
            onClick={captureFrame}
            className="h-13 w-full bg-gradient-to-r from-blue-600 to-sky-600 text-white text-[15px] font-bold rounded-2xl mt-4 shadow-lg shadow-blue-600/25 transition-all active:scale-[0.98] border border-blue-400/30 flex items-center justify-center gap-2 cursor-pointer"
          >
            <Camera className="h-4 w-4" /> Snap Picture
          </button>
        </div>
      )}

      {/* Stage: Crop & Adjust */}
      {stage === "crop" && (
        <div className="mt-2 animate-in fade-in duration-200">
          <div className="mb-5 p-4 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_20px_rgba(37,99,235,0.06)]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-extrabold uppercase tracking-widest text-slate-800">
                Front Side <span className="text-blue-600">*</span>
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleApplyPresetCrop("horizontal", false)}
                  className="px-2.5 py-1 text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 rounded-xl hover:bg-blue-100 transition cursor-pointer"
                >
                  3.5:2 Card
                </button>
                <button
                  type="button"
                  onClick={() => handleApplyPresetCrop("square", false)}
                  className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-200 transition cursor-pointer"
                >
                  1:1 Square
                </button>
                <button
                  type="button"
                  onClick={() => handleResetFullImageCrop(false)}
                  className="px-2 py-1 text-[11px] font-bold bg-slate-100 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-200 transition cursor-pointer"
                >
                  <Maximize2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            <div className="bg-slate-950/90 rounded-2xl overflow-hidden border border-slate-200 flex justify-center p-2 shadow-inner">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                className="max-h-[42vh]"
              >
                <img
                  src={imageData}
                  ref={imgRef}
                  alt="Crop preview"
                  className="max-w-full rounded-xl object-contain"
                  onLoad={(e) => {
                    const { naturalWidth, naturalHeight } = e.currentTarget;
                    const autoBox = calculateAutoCardCrop(naturalWidth, naturalHeight, cardOrientation);
                    setCrop(autoBox.percent);
                    setCompletedCrop(autoBox.pixel);
                  }}
                />
              </ReactCrop>
            </div>
          </div>

          {/* Optional Back Side */}
          <div className="mb-5 p-4 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-extrabold uppercase tracking-widest text-slate-800">
                Back Side <span className="text-slate-400 font-normal">(Optional)</span>
              </span>

              {backImageData && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleApplyPresetCrop("horizontal", true)}
                    className="px-2.5 py-1 text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 rounded-xl cursor-pointer"
                  >
                    3.5:2
                  </button>
                  <button
                    type="button"
                    onClick={() => setBackImageData(null)}
                    className="text-[11px] font-bold text-red-500 hover:text-red-600 px-1 cursor-pointer"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {!backImageData ? (
              <div className="grid grid-cols-2 gap-2.5">
                <input
                  ref={backFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                  onChange={(e) => handleFile(e.target.files?.[0], true)}
                />
                <button
                  type="button"
                  onClick={(e) => handleOpenCamera(e, true)}
                  className="h-11 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl flex items-center justify-center gap-2 border border-slate-200/80 transition cursor-pointer"
                >
                  <Camera className="h-4 w-4 text-blue-600" /> Camera
                </button>
                <button
                  type="button"
                  onClick={() => backFileRef.current?.click()}
                  className="h-11 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl flex items-center justify-center gap-2 border border-slate-200/80 transition cursor-pointer"
                >
                  <Upload className="h-4 w-4 text-slate-500" /> Upload
                </button>
              </div>
            ) : (
              <div className="bg-slate-950/90 rounded-2xl overflow-hidden border border-slate-200 flex justify-center p-2 shadow-inner">
                <ReactCrop
                  crop={backCrop}
                  onChange={(c) => setBackCrop(c)}
                  onComplete={(c) => setCompletedBackCrop(c)}
                  className="max-h-[30vh]"
                >
                  <img
                    src={backImageData}
                    ref={backImgRef}
                    alt="Back crop preview"
                    className="max-w-full rounded-xl object-contain"
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      const autoBox = calculateAutoCardCrop(naturalWidth, naturalHeight, cardOrientation);
                      setBackCrop(autoBox.percent);
                      setCompletedBackCrop(autoBox.pixel);
                    }}
                  />
                </ReactCrop>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleScanImage}
            disabled={isScanning}
            className="h-13 w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white text-[15px] font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25 transition-all active:scale-[0.98] border border-blue-400/30 cursor-pointer disabled:opacity-60"
          >
            {isScanning ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" /> Processing OCR…
              </>
            ) : (
              <>
                <ScanLine className="h-4 w-4" /> Extract Lead Info
              </>
            )}
          </button>
        </div>
      )}

      {/* Stage: Scanning Animation */}
      {stage === "scanning" && (
        <div className="mt-8 px-1">
          <div className="relative mb-6 rounded-3xl overflow-hidden bg-slate-950 p-2 shadow-2xl border border-blue-200/50">
            <div className="aspect-[1.586/1] rounded-2xl overflow-hidden relative">
              <img
                src={finalImage || imageData}
                alt="card"
                className="w-full h-full object-cover opacity-50 blur-[1px]"
              />
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-[0_0_20px_#38bdf8] animate-[scan_1.5s_ease-in-out_infinite]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="px-4 py-2 rounded-full bg-slate-900/80 border border-blue-500/40 backdrop-blur-xl text-blue-300 text-xs font-bold tracking-wide flex items-center gap-2 shadow-xl">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-400" />
                  <span>Extracting contact info…</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stage: Review Details */}
      {stage === "review" && (
        <div className="mt-2 space-y-4 animate-in fade-in duration-200">
          <div className="p-5 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_20px_rgba(37,99,235,0.06)]">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 min-w-0">
                <h2 className="text-2xl font-extrabold tracking-tight text-slate-950 truncate">
                  {form.name || "Unnamed Contact"}
                </h2>
                <div className="flex flex-wrap items-center gap-x-2 text-xs font-semibold text-slate-600">
                  {form.job_title && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span>{form.job_title}</span>
                    </span>
                  )}
                  {form.job_title && form.company && <span>·</span>}
                  {form.company && (
                    <span className="flex items-center gap-1 font-bold text-blue-700">
                      <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span>{form.company}</span>
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={reset}
                className="shrink-0 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition cursor-pointer"
                title="Rescan card"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            {form.email && (
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
                <div className="flex items-center gap-3.5 min-w-0 pr-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Email</p>
                    <p className="text-[14px] font-semibold text-slate-900 truncate tracking-tight">{form.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(form.email, "email")}
                  className="p-2 text-slate-400 hover:text-blue-600 cursor-pointer"
                >
                  {copiedKey === "email" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            )}

            {form.phone && (
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
                <div className="flex items-center gap-3.5 min-w-0 pr-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Mobile (WhatsApp)</p>
                    <p className="text-[14px] font-semibold text-slate-900 truncate tracking-tight font-mono-num">{form.phone}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(form.phone, "phone")}
                  className="p-2 text-slate-400 hover:text-blue-600 cursor-pointer"
                >
                  {copiedKey === "phone" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            )}

            {form.website && (
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
                <div className="flex items-center gap-3.5 min-w-0 pr-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Website</p>
                    <p className="text-[14px] font-semibold text-slate-900 truncate tracking-tight">{form.website}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(form.website, "website")}
                  className="p-2 text-slate-400 hover:text-blue-600 cursor-pointer"
                >
                  {copiedKey === "website" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            )}

            {form.address && (
              <div className="flex items-start justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
                <div className="flex items-start gap-3.5 min-w-0 pr-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25 mt-0.5">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Office Address</p>
                    <p className="text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap tracking-tight mt-0.5">{form.address}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(form.address, "address")}
                  className="p-2 text-slate-400 hover:text-blue-600 mt-0.5 cursor-pointer"
                >
                  {copiedKey === "address" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>

          {/* Editable Fields Form */}
          <div className="p-5 rounded-3xl bg-white/95 backdrop-blur-xl border border-blue-100 shadow-md flex flex-col gap-3.5">
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-blue-600 flex items-center gap-1.5 mb-1">
              <Edit2 className="w-3.5 h-3.5" /> Verify & Edit Lead Information
            </p>
            <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Job Title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} />
            <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Mobile Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
            <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Field label="Secondary Phone" value={form.phone2} onChange={(v) => setForm({ ...form, phone2: v })} />
            <Field label="Secondary Email" value={form.email2} onChange={(v) => setForm({ ...form, email2: v })} />
          </div>

          {/* Tags & Notes */}
          <div className="p-5 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-blue-600 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Smart Tags & Conversation Notes
            </p>
            <TagPicker value={tags} onChange={setTags} />
            <textarea
              className="w-full mt-3 p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition min-h-[4.5rem]"
              placeholder="Met at booth #42, interested in high-volume orders..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Follow-up Reminder */}
          <div className="p-5 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-2xs">
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-blue-600 mb-2 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Follow-Up Reminder (Optional)
            </p>
            <div className="flex gap-2">
              <input
                type="datetime-local"
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                className="w-full h-11 px-3.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition"
              />
              {reminder && (
                <button
                  type="button"
                  onClick={() => setReminder("")}
                  className="h-11 px-3.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 active:scale-95 cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Duplicate Lead Alert Banner */}
          {duplicateWarning && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Duplicate Lead Prevented</p>
                <p className="mt-0.5 text-amber-800">{duplicateWarning}</p>
              </div>
            </div>
          )}

          {/* Save Action Button */}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-13 w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white text-[15px] font-bold rounded-2xl shadow-lg shadow-blue-600/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2 border border-blue-400/30 disabled:opacity-60 cursor-pointer"
          >
            {saving ? (
              <>
                <RefreshCw className="animate-spin h-4 w-4 text-white" /> Saving & Dispatching WhatsApp…
              </>
            ) : (
              "Save to Database"
            )}
          </button>
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes scan {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(180px); }
        }
      `,
        }}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label.toLowerCase()}…`}
        className="w-full h-11 px-3.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition"
      />
    </div>
  );
}